import { Injectable } from '@angular/core';
import {
  LBSErrorHandler,
  LBSErrorHandlerResult,
} from '../../error-handler/error-handler';
import { LoggingService } from '../../logging/logic/logging.service';
import { TerminalError } from './terminal-errors';
import { CapacitorException } from '@capacitor/core';
import { RemoteActionService } from '../../remote-action/logic/remote-action.service';
import { ActionInterpreterService } from '../../action-interpreter/action-interpreter.service';

@Injectable({
  providedIn: 'root',
})
export class TerminalErrorHandler implements LBSErrorHandler {
  // Lifecycle
  constructor(private logger: LoggingService) {}

  public async handleError(error: Error): Promise<LBSErrorHandlerResult> {
    console.log('JBErrorHandling TerminalErrorHandler', error.message, error);
    if (
      Object.values(TerminalError).includes(error?.message as TerminalError)
    ) {
      return await this.getErrorHandlerResultForKey(
        error as CapacitorException,
      );
    }
    this.logger.error(
      this.constructor.name,
      'given error',
      error?.message,
      'is not of type TerminalError',
    );
    return undefined;
  }

  // Private Functions
  public async getErrorHandlerResultForKey(
    error: CapacitorException,
  ): Promise<LBSErrorHandlerResult> {
    switch (error.message as TerminalError) {
      case TerminalError.LOGIN_FAILED:
        return {
          action: {
            type: 'action',
            target: 'logterminalconnectionproblem',
            params: {
              terminalId: error.data?.tid,
              terminalError: error.code,
            },
          },
        };
      case TerminalError.HARDWARE_INFO_FAILED:
        // Do nothing, Battery Status will not be up to date
        return {};
      case TerminalError.BALANCE_FAILED:
        this.logger.error(
          this.constructor.name,
          `Balance failed: ${error.code} ${this.createLogMessageAttachment(error)}`,
        );
        return {
          alert: {
            title: 'ERROR-HANDLERS.TERMINAL-ERRORS.BALANCE_FAILED.TITLE',
            subtitle: 'ERROR-HANDLERS.TERMINAL-ERRORS.BALANCE_FAILED.SUBTITLE',
            message: `${error.code}`,
            messageTranslate: false,
            confirm: 'ERROR-HANDLERS.TERMINAL-ERRORS.BALANCE_FAILED.CONFIRM',
            confirmFunc: () => {},
            showExportLog: false,
          },
        };
      case TerminalError.TRANSACTION_FAILED:
        this.logger.error(
          this.constructor.name,
          `Transaction failed: ${error.code} ${this.createLogMessageAttachment(error)}`,
        );
        return {
          alert: {
            title: 'ERROR-HANDLERS.TERMINAL-ERRORS.TRANSACTION_FAILED.TITLE',
            subtitle:
              'ERROR-HANDLERS.TERMINAL-ERRORS.TRANSACTION_FAILED.SUBTITLE',
            message: `${error.code}`,
            messageTranslate: false,
            confirm:
              'ERROR-HANDLERS.TERMINAL-ERRORS.TRANSACTION_FAILED.CONFIRM',
            confirmFunc: () => {},
            showExportLog: false,
          },
        };
      case TerminalError.NO_TERMINAL:
        return {
          alert: {
            title: 'ERROR-HANDLERS.TERMINAL-ERRORS.NO_TERMINAL.TITLE',
            subtitle: 'ERROR-HANDLERS.TERMINAL-ERRORS.NO_TERMINAL.SUBTITLE',
            message: 'ERROR-HANDLERS.TERMINAL-ERRORS.NO_TERMINAL.MESSAGE',
            confirm: 'ERROR-HANDLERS.TERMINAL-ERRORS.NO_TERMINAL.CONFIRM',
            confirmFunc: () => {},
            showExportLog: false,
            cancel: "ERROR-HANDLERS.TERMINAL-ERRORS.NO_TERMINAL.OPEN-PERIPHERALS",
            cancelAction: {
              type: 'navigation',
              target: 'peripherals',
            }
          },
        };
      default:
        this.logger.error(
          this.constructor.name,
          'Not Handled PeripheralError found!',
          error?.message,
        );
        return undefined;
    }
  }

  private createLogMessageAttachment(error: CapacitorException): String {
    let logMessage = '(';
    if (error.data?.paymentProtocol) {
      logMessage += `PaymentProtocol: ${error.data.paymentProtocol};`;
    }
    if (error.data?.ppEp2AuthReslt) {
      logMessage += `Ep2AuthResult: ${error.data.ppEp2AuthReslt};`;
    }
    if (error.data?.ppEp2AuthRespC) {
      logMessage += `Ep2AuthRespCode: ${error.data.ppEp2AuthRespC};`;
    }
    if (error.data?.nativeErrorCode) {
      logMessage += `NativeErrorCode: ${error.data.nativeErrorCode};`;
    }
    if (error.data?.nativeErrorMessage) {
      logMessage += `NativeErrorMessage: ${error.data.nativeErrorMessage};`;
    }
    if (error.data?.nativeErrorSource) {
      logMessage += `NativeErrorSource: ${error.data.nativeErrorSource};`;
    }
    logMessage += ')';
    return logMessage;
  }
}
